// src/pages/AirportManagementPage.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Tooltip,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Chip,
} from '@mui/material';
import {
  Edit,
  Delete,
  MoreVert,
  Add,
  Search,
  AccessTime,
  People,
} from '@mui/icons-material';
import {
  getAdminAirports,
  deleteAirport,
  Airport,
  AirportApiError,
} from '../services/airportApi';
import EditAirportDialog from '../components/Airport/EditAirportDialog';
import { useAuth } from '../contexts/AuthContext';
import { hasRole } from '../services/authApi';

export function AirportManagementPage() {
  const { user } = useAuth();
  const [airports, setAirports] = useState<Airport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Only System Admin can edit/delete airports (not Exec Admin)
  const canEditAirports = user && hasRole(user, 'System Admin');
  const canDeleteAirports = user && hasRole(user, 'System Admin');
  
  // Search
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [total, setTotal] = useState(0);
  
  // Menu and dialogs
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuAirportId, setMenuAirportId] = useState<number | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingAirport, setEditingAirport] = useState<Airport | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingAirport, setDeletingAirport] = useState<Airport | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // Load airports
  const loadAirports = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const params: {
        page?: number;
        pageSize?: number;
        search?: string;
      } = {
        page,
        pageSize,
      };
      
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      
      const response = await getAdminAirports(params);
      setAirports(response.airports);
      setTotal(response.total);
    } catch (err) {
      const apiError = err as AirportApiError;
      setError(apiError.message || 'Failed to load airports');
      console.error('Error loading airports:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAirports();
  }, [page]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 1) {
        loadAirports();
      } else {
        setPage(1); // Reset to first page when search changes
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, airportId: number) => {
    setMenuAnchor(event.currentTarget);
    setMenuAirportId(airportId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuAirportId(null);
  };

  const handleCreate = () => {
    setEditingAirport(null);
    setCreateDialogOpen(true);
    handleMenuClose();
  };

  const handleEdit = (airport: Airport) => {
    setEditingAirport(airport);
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleEditSuccess = () => {
    setEditDialogOpen(false);
    setCreateDialogOpen(false);
    setEditingAirport(null);
    setSuccessMessage('Airport saved successfully');
    loadAirports();
  };

  const handleDeleteClick = (airport: Airport) => {
    setDeletingAirport(airport);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!deletingAirport) return;
    try {
      setActionLoading(deletingAirport.airportId);
      await deleteAirport(deletingAirport.airportId);
      setDeleteDialogOpen(false);
      setDeletingAirport(null);
      setSuccessMessage('Airport deleted successfully');
      loadAirports();
    } catch (err) {
      const apiError = err as AirportApiError;
      setError(apiError.message || 'Failed to delete airport');
      console.error('Error deleting airport:', err);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: 'white' }}>
          Airport Management
        </Typography>
        {canEditAirports && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
          >
            Create Airport
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          placeholder="Search by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ minWidth: 250, flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Airports Table */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Timezone</TableCell>
                  <TableCell>Managers</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {airports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No airports found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  airports.map((airport) => (
                    <TableRow key={airport.airportId}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {airport.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {airport.timezone ? (
                          <Chip
                            icon={<AccessTime />}
                            label={airport.timezone}
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            UTC (default)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {airport.managers && airport.managers.length > 0 ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {airport.managers.slice(0, 2).map((manager) => (
                              <Chip
                                key={manager.userId}
                                icon={<People />}
                                label={manager.displayName || manager.email}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                            {airport.managers.length > 2 && (
                              <Chip
                                label={`+${airport.managers.length - 2} more`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No managers
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="More actions">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, airport.airportId)}
                            disabled={actionLoading === airport.airportId}
                          >
                            <MoreVert />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {total > pageSize && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={Math.ceil(total / pageSize)}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {menuAirportId && (() => {
          const airport = airports.find(a => a.airportId === menuAirportId);
          if (!airport) return null;
          
          return (
            <>
              {canEditAirports && (
                <MenuItem onClick={() => handleEdit(airport)}>
                  <Edit sx={{ mr: 1 }} />
                  Edit Airport
                </MenuItem>
              )}
              {canDeleteAirports && (
                <MenuItem onClick={() => handleDeleteClick(airport)}>
                  <Delete sx={{ mr: 1 }} />
                  Delete Airport
                </MenuItem>
              )}
            </>
          );
        })()}
      </Menu>

      {/* Edit/Create Airport Dialog */}
      {(editDialogOpen || createDialogOpen) && (
        <EditAirportDialog
          open={editDialogOpen || createDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setCreateDialogOpen(false);
            setEditingAirport(null);
          }}
          onSuccess={handleEditSuccess}
          airport={editingAirport}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Airport</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{deletingAirport?.name}</strong>?
            This action cannot be undone. Projects associated with this airport will need to be updated.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={actionLoading !== null}
          >
            {actionLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

